/**
 * "YOU CAN'T SEE THIS" — as a written Hebrew screen. 09 §5.8, decision 8.
 *
 *   "An external customer never sees a raw 403. Every 'you can't see this' is a written Hebrew
 *    screen that says what to do next."
 *
 * Two layers, and the difference is the whole point (packages/contracts/src/decision.ts, CF-37):
 *   inside the tenant  — the first failing stage's reason, named, with one next step.
 *   outside the tenant — `reason` never arrives, and this component says exactly one uniform
 *                        sentence. It reveals nothing: not the stage, not whether the object exists.
 *
 * This component renders a decision the SERVER made. It contains no permission logic, and adding
 * any would be the defect the whole gate design exists to prevent.
 *
 * ------------------------------------------------------------------------------------------------
 *  THE WAY OUT DEPENDS ON WHICH SURFACE WAS REFUSED. **X8.**
 * ------------------------------------------------------------------------------------------------
 *
 * Seven rows in `denialText.ts` — `UNIFORM_DENIAL` and six in `DENIAL_TEXT` — answer
 * `action: 'back_to_companies'` with *"חזרה לחברות שלי"*. On the PLATFORM console that is wrong in
 * the one way a refusal must never be wrong: it sends a refused Platform Operator to `/companies`, a
 * TENANT address their session has no business at, so the screen that exists to say *what to do next*
 * names a place they will be refused again. The Platform Owner is the single account that cannot be
 * helped by anybody, on the surface with no second operator to ask.
 *
 * THE OVERRIDE LIVES HERE, NOT IN THE COPY. `UNIFORM_DENIAL` and `DENIAL_TEXT` are byte-identical:
 * they are the gate's refusal vocabulary, shared with the office's own screens, and a surface branch
 * inside them would make one map answer two audiences — which is how a uniform denial stops being
 * uniform. One rule in one component covers all seven rows, and a new `back_to_companies` row is
 * covered the day it is added.
 *
 * IT BRANCHES ON THE SURFACE THE SERVER STATED, VIA `landingFor`. Not on `DenyBody` — which carries
 * `error · reason · stage · requestUid` and nothing about where the caller is — and not on a
 * capability, which would be this component deciding something. `WorkContext.surface` is
 * `session.sessionContext` and nothing else (`ContextProvider.tsx:181`), and `landingFor` is already
 * the ONE place that answers "where does a session of this context belong".
 *
 * `useWorkContext()` throws outside `<ContextProvider>`, and that is acceptable here rather than
 * incidental: a denial is a server response to a request made from inside the shell, so there is no
 * render path for this component that is not already inside the provider. An optional `surface` prop
 * would have been the alternative, and it is the worse one — every call site that forgot to pass it
 * would silently keep the tenant wording on the platform console.
 */

import type { DenyBody } from '../api/contract';
import { denialTextFor } from '../api/denialText';
import type { DenialActionKind } from '../api/denialText';
import { landingFor } from '../shell/landing';
import { routes } from '../routes';
import { StepUpPrompt } from './StepUpPrompt';
import { SupportReference } from './SupportReference';
import { useWorkContext } from '../shell/ContextProvider';

/**
 * The platform console's own way back. **Supplied copy — AMEND-3 §C-4.**
 *
 * Only ONE string is declared here. The tenant label is `denialText.ts`'s own `nextStepHe`, read from
 * the row rather than copied, so there is no second recording of it to drift.
 */
const PLATFORM_BACK_HE = 'חזרה לקונסולת הפלטפורמה';

export interface DenialNoticeProps {
  /** The gate's body verbatim, as `DeniedError.body` delivered it. */
  readonly deny: DenyBody;
  /** Wired by the screen: what "next step" actually does. Absent ⇒ the step is stated, not clickable. */
  readonly onAction?: (action: DenialActionKind) => void;
  /**
   * Run after a step-up has been satisfied HERE. **Section 3, 2026-10-25.**
   *
   * The Owner on `/tenant/users`: *"״אימות זהות והמשך״ הוא טקסט שאינו לחיץ. אין דרך להמשיך."* It
   * was text because there was nowhere for it to lead — no route let a signed-in person present
   * their authenticator, so every `per_action` capability went unreachable minutes after sign-in.
   *
   * Given this, the refusal renders the code field INLINE and the screen retries on success. Not
   * given, the step is still stated — the same behaviour as before, for screens not yet wired.
   */
  readonly onStepUpSatisfied?: () => void;
}

export function DenialNotice({
  deny,
  onAction,
  onStepUpSatisfied,
}: DenialNoticeProps): JSX.Element {
  const text = denialTextFor(deny.reason);
  const { surface } = useWorkContext();

  /*
   * `null` until the server has answered whoami, and that is a real third state rather than a
   * default: a guessed surface here would label the way out of a refusal after guessing which
   * product the person is in. With no surface the step is stated, exactly as it was before.
   */
  const back =
    text.action === 'back_to_companies' && surface !== null
      ? {
          /*
           * THE TENANT HALF NAMES THE COMPANIES SCREEN, not the landing.
           *
           * It read landingFor(surface) for both, which was right only while the tenant landing
           * WAS the company list. The approved customer workspace adds a home of its own, so the
           * two addresses parted -- and this action is called back_to_companies and is worded
           * chazara la-chavarot sheli. Sending it to the workspace home would have made the link
           * disagree with its own label, silently, the day the landing moved.
           */
          href: surface === 'PLATFORM' ? landingFor(surface) : routes.companies,
          labelHe: surface === 'PLATFORM' ? PLATFORM_BACK_HE : text.nextStepHe,
        }
      : null;

  return (
    <section className="denial" data-tone={text.tone} role="alert">
      <h2 className="denial__title">{text.titleHe}</h2>
      <p className="denial__explanation">{text.explanationHe}</p>

      {/*
        ── THE STEP-UP IS COMPLETED HERE, NOT SOMEWHERE ELSE. **Section 3, 2026-10-25.** ─────────

        Inline, because the requirement is to come back to the original action *"תוך שמירת פרטים
        שהוזנו"* — and a navigation to a challenge screen unmounts this screen and everything typed
        into it. Staying put is what preserves them.

        It is checked BEFORE the branches below, so a step-up refusal never falls through to the
        plain-text next step that started this.
      */}
      {deny.reason === 'stepup_required' ? (
        /*
         * ALWAYS RENDERED, NOT ONLY WHERE A SCREEN OPTED IN. Nineteen screens render this component
         * and the Owner asked for the others to be checked too. Wiring them one at a time would
         * have left the control present on some and dead text on others, for a reason invisible
         * from the screen — which is the defect that produced this section.
         *
         * A screen that passes `onStepUpSatisfied` retries precisely: it re-reads what it needs and
         * keeps what was typed. One that has not been wired falls back to reloading the address,
         * which is this product's ordinary navigation anyway — every in-app navigation here is a
         * full page load. That is worse than a precise retry and far better than no way forward.
         */
        <StepUpPrompt
          onSatisfied={
            onStepUpSatisfied ??
            (() => {
              window.location.reload();
            })
          }
        />
      ) : onAction !== undefined && text.action !== 'none' ? (
        <button type="button" className="btn btn--primary" onClick={() => onAction(text.action)}>
          {text.nextStepHe}
        </button>
      ) : back !== null ? (
        // The anchor shape `CompanyHomeScreen.tsx:42` already uses for this exact journey — a full
        // page load, which is what every in-app navigation in this build is.
        <a className="btn" href={back.href}>
          {back.labelHe}
        </a>
      ) : (
        <p className="denial__next-step">{text.nextStepHe}</p>
      )}

      {/*
        ── THE CODE IS BEHIND A DISCLOSURE, AND IT CAN BE COPIED. **Owner section 5, 2026-10-28.** ─

            *"הודעת חוסר ההרשאה צריכה להיות קצרה וקומפקטית, עם פעולה רלוונטית. קוד
            התמיכה יהיה בפרטים נפתחים עם אפשרות העתקה."*

        It was a line of small print carrying a 20-character machine string, on every refusal,
        under a message whose whole job is to be read in one breath. Most people never need it; the
        one who does needs to hand it over EXACTLY, which is what the copy button is for.

        `<details>` and not a hand-built toggle: the browser's own disclosure is keyboard-reachable,
        announced by a screen reader, and findable by in-page search even while closed — so
        somebody who was told "give me the code from the message" can still find it with Ctrl+F.

        The `<summary>` names it, so `labelled={false}`: the label would otherwise appear twice,
        once on the summary and once inside it.
      */}
      {deny.requestUid === '' ? null : (
        <details className="denial__ref">
          <summary className="denial__ref-summary">קוד לפנייה לתמיכה</summary>
          {/*
            09 §9.1 V3: a short reference code is the ONLY technical string a customer ever sees.
            Never a route, never a path, never an exception type, never a status code.
          */}
          <SupportReference requestUid={deny.requestUid} className="denial__reference" labelled={false} />
        </details>
      )}
    </section>
  );
}
